import { useState } from "react";
import { FrameworkControlSaveModel } from "./CreateEditFrameworkControlDialog.types";
import { useResponsibleQuery } from "../../../user/hooks/useResponsibleQuery";
import { post, put } from "../../../common/api/apiShared";
import { Result } from "../../../common/api/result";
import { CreateEditFrameworkControlDialogProps } from "./CreateEditFrameworkControlDialog";
import { useSelectableFrameworkCategories } from "../../../common/hooks/useSelectableItemQueries";
import { useValidator } from "../../../common/hooks/validation";
import { useTranslation } from "../../../localization/useTranslation";
import { isNullOrWhitespace } from "../../../common/stringOperations";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useCreateEditFrameworkControlDialog(props: CreateEditFrameworkControlDialogProps) {
    const { translateString } = useTranslation();
    const [saveModel, setSaveModel] = useState(getDefaultSaveModel());

    const coordinatorQuery = useResponsibleQuery();
    const frameworkCategoryQuery = useSelectableFrameworkCategories(props.frameworkId);

    const validation = useValidator<FrameworkControlSaveModel>((validators) => ({
        name: validators.validateNotEmpty((item) => item.name, translateString("name")),
        categoryId: validators.validateNotEmpty((item) => item.categoryId, translateString("category")),
        coordinatorId: validators.validateNotEmpty((item) => item.coordinatorId, translateString("controlCoordinator")),
    }));

    validation.check(saveModel);

    const isEdit = !isNullOrWhitespace(props.selectedControl?.id);

    const saveMutation = useDotLegalMutation(save, {
        onSuccess: (response: any) => {
            const id = response.value().id;
            props.onSaved(id);
        },
    });

    function save() {
        return post<string>(`/frameworkControls/${props.frameworkId}/`, saveModel);
    }

    const updateMutation = useDotLegalMutation(update, {
        onSuccess: (response: any) => {
            props.onSaved("");
        },
    });

    function update() {
        return put<string>(`/frameworkControls/${props.selectedControl?.id}/`, saveModel);
    }

    const createControl = async () => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        if (isEdit) {
            await updateMutation.mutateAsync();
        } else {
            await saveMutation.mutateAsync();
        }
    };

    const modelUpdater = {
        onNameChange: (name: string | undefined) => {
            const updatedModel = saveModel.withUpdates({ name });
            setSaveModel(updatedModel);
        },
        onDescriptionChange: (description: string | undefined) => {
            const updatedModel = saveModel.withUpdates({ description });
            setSaveModel(updatedModel);
        },
        onCategoryChange: (categoryId: string | null) => {
            const updatedModel = saveModel.withUpdates({ categoryId: categoryId ?? undefined });
            setSaveModel(updatedModel);
        },
        onImplementationGroupChange: (implementationGroup: string) => {
            const updatedModel = saveModel.withUpdates({ implementationGroup });
            setSaveModel(updatedModel);
        },
        onCustomIdChange: (customId: string | undefined) => {
            const updatedModel = saveModel.withUpdates({ customId });
            setSaveModel(updatedModel);
        },
        onCoordinatorChange: (coordinatorId: string | null) => {
            const updatedModel = saveModel.withUpdates({ coordinatorId: coordinatorId ?? undefined });
            setSaveModel(updatedModel);
        },
        onTagsChange: (tagIds: string[]) => {
            const updatedModel = saveModel.withUpdates({ tagIds: tagIds });
            setSaveModel(updatedModel);
        },
    };

    return {
        isEdit,
        modelUpdater,
        saveModel,
        coordinatorOptions: coordinatorQuery.userData,
        coordinatorOptionsIsLoading: coordinatorQuery.isLoading,
        categoryOptions: frameworkCategoryQuery.data,
        categoryOptionsIsLoading: frameworkCategoryQuery.isLoading,
        createControl,
        validation,
        isSaving: saveMutation.isLoading || updateMutation.isLoading,
    };

    function getDefaultSaveModel() {
        return new FrameworkControlSaveModel(
            props.selectedControl?.name ?? undefined,
            props.selectedControl?.description ?? undefined,
            props.selectedControl?.categoryId ?? undefined,
            props.selectedControl?.customId ?? undefined,
            props.selectedControl?.coordinatorId ?? undefined,
            props.selectedControl?.tagIds ?? [],
            props.selectedControl?.implementationGroup ?? undefined
        );
    }
}
